import { AsyncPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { Observable, first, Subject, takeUntil } from 'rxjs';
import { IGridState, IBaseSearchRequest, PageSize } from 'src/app/core/models/base';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { IStartTimeEntryRequest, ITimeEntry } from 'src/app/time-tracking/models/time-entry';
import { TimeEntryService } from 'src/app/time-tracking/services/time-entry.service';
import { TimeEntriesStorageService } from 'src/app/time-tracking/storage/time-entries-storage.service';
import { IAssignment } from '../../models/assignment';
import { IService } from '../../models/service';
import { AssignmentServicesService } from '../../services/assignment-service.service';
import { ServiceService } from '../../services/service.service';
import { AssignmentStorageService } from '../../storage/assignment-storage.service';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { flexibleTimeValidator } from 'src/app/shared/validators/timeStringValidator.validator';
@UntilDestroy()
@Component({
    selector: 'app-assignment-services',
    templateUrl: './assignment-services.component.html',
    styleUrls: ['./assignment-services.component.css'],
    standalone: false
})
export class AssignmentTimeServicesComponent implements OnInit {

  constructor(
    private assignmentStore: AssignmentStorageService,
    private assignmentServicesService : AssignmentServicesService,
    private servicesService: ServiceService,
    private asyncPipe : AsyncPipe,
    private appnotificationService: AppNotificationService,
    private timeEntryService: TimeEntryService,
    private timeEntriesStorage : TimeEntriesStorageService,
  ) { }

  ngOnInit(): void {
    this.servicesService.searchServices({pageNumber: 0, pageSize: 999999})
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      const data = response.data

      data.map(x => x.defaultTimeObj = TimeSpan.parse(x.defaultTime))
     
      this.services = data

    })

    this.hasRunningTimeEntry();
  }
  
  destroy$ = new Subject();

  public assignment$: Observable<IAssignment | null> =
    this.assignmentStore.assignment$;

  timeEntries$ : Observable<ITimeEntry[]> =
    this.timeEntriesStorage.timeEntries$;

  public services : IService[];

  public formGroup!: UntypedFormGroup;
  private editedRowIndex!: number;

  timeEntryisRunning;
  assignmentServiceTimeEntryId : string = null;

  gridState: IGridState = {
    pageSize: 25,
    skip: 0,
  };

  searchRequest: IBaseSearchRequest = {
    pageNumber: 0,
    pageSize: 25,
    orderBy:['isCompleted']
  }

  get pageSizes() {
    return PageSize;
  }

  updateList(){
    this.assignmentStore.getAssignment(this.asyncPipe.transform(this.assignment$).id);
  }

  addHandler({ sender }){
    this.closeEditor(sender);

    this.formGroup = new UntypedFormGroup({
      id: new UntypedFormControl(null),
      remarks: new UntypedFormControl(null, Validators.required),
      time: new UntypedFormControl(null,flexibleTimeValidator()),
      serviceId: new UntypedFormControl(null),
      assignmentId: new UntypedFormControl(null),
      isCompleted: new UntypedFormControl(false),
    });

    this.initRemarkValueBasedOnService();

    sender.addRow(this.formGroup);
  }

  editHandler({ sender, rowIndex, dataItem }){
    this.closeEditor(sender);

    this.formGroup = new UntypedFormGroup({
      id: new UntypedFormControl(dataItem.id),
      remarks: new UntypedFormControl(dataItem.remarks, Validators.required),
      time: new UntypedFormControl(dataItem.timeObj.toStringShort(), flexibleTimeValidator()),
      isCompleted: new UntypedFormControl(dataItem.isCompleted, Validators.required),
      serviceId: new UntypedFormControl(dataItem.service),
    });

    this.initRemarkValueBasedOnService();

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  public saveHandler(event) {
    const request: any = {
      remarks: event.formGroup.value.remarks,
      isCompleted: event.formGroup.value.isCompleted,
      serviceId: event.formGroup.value.serviceId?.id,
      time: event.formGroup.value.time ? TimeSpan.parse(event.formGroup.value.time).toString() : null,
      id: event.dataItem.id ?? null,
      assignmentId: this.asyncPipe.transform(this.assignment$).id,
    }
    if (!event.dataItem.id){
      this.assignmentServicesService.createAssignmentService(request)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.updateList();
        this.appnotificationService.notification$.next(
          {message: translate('assignment.service.createSuccess'), style: 'success'}
          )
      })
    } else {
      this.assignmentServicesService.updateAssignmentService(request)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.updateList();
        this.appnotificationService.notification$.next(
          {message: translate('assignment.service.saveSuccess'), style: 'success'}
          )
      })
    }

    event.sender.closeRow(event.rowIndex);
    
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  public removeHandler({dataItem}) {
    // remove the current dataItem from the current data source,
    // `editService` in this example
    this.assignmentServicesService.deleteAssignmentService(dataItem.id)
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.updateList();
      this.appnotificationService.notification$.next(
        {message: translate('assignment.service.deleteSuccess'), style: 'success'}
        )
    });;
  }

  onPageSizeChange(pageSize: number) {
    this.onPageChange({ skip: this.gridState.skip, take: pageSize });
  }

  onGridReload() {
    this.updateList();
  }

  onPageChange({ skip, take }: PageChangeEvent) {
    const page = Math.round((skip + take) / take);

    this.gridState.skip = skip;
    this.gridState.pageSize = take;

    this.searchRequest.pageNumber = page;
    this.searchRequest.pageSize = take;

    this.updateList();
  }

  initRemarkValueBasedOnService(){
    this.formGroup.get('serviceId').valueChanges
      .pipe(
        takeUntil(this.destroy$),
        untilDestroyed(this))
      .subscribe(value =>{
        this.formGroup.get('remarks').patchValue(value.name);
        this.formGroup.get('time').patchValue(TimeSpan.parse(value.defaultTime).toStringShort());
      } )
  }

  onServiceTimeTracking(dataItem){


    if(this.timeEntryisRunning){
      this.timeEntryService.stopTimeEntry()
        .subscribe(response => {
          this.timeEntriesStorage.listTimeEntriesOfCurrentUserToday();
          this.assignmentStore.getAssignment(this.asyncPipe.transform(this.assignment$).id);
        })
    } else {
      var request : IStartTimeEntryRequest = {
        timeEntryCategoryId: dataItem.service.timeEntryCategoryId,
        assignmentId: this.asyncPipe.transform(this.assignment$).id,
        assignmentServiceId: dataItem.id
      };
      this.timeEntryService.startTimeEntry(request)
      .pipe(
        untilDestroyed(this)
      ).subscribe(response => {
        this.timeEntriesStorage.listTimeEntriesOfCurrentUserToday();
        this.assignmentStore.getAssignment(this.asyncPipe.transform(this.assignment$).id);
      })
    }

  }

  hasRunningTimeEntry(){
    this.timeEntries$.pipe(untilDestroyed(this))
    .subscribe(timeEntries => {
      const activeTimeEntry = timeEntries.find((timeEntry) => !timeEntry.stop);

      if(activeTimeEntry?.assignmentId !=
         this.asyncPipe.transform(this.assignment$)?.id && 
         this.asyncPipe.transform(this.assignment$)){
          this.timeEntryisRunning = false;
          this.assignmentServiceTimeEntryId = null;
      } else if(activeTimeEntry){
        this.timeEntryisRunning = true
        this.assignmentServiceTimeEntryId = activeTimeEntry.assignmentServiceId;
      } 
      else {
        this.timeEntryisRunning = false;
        this.assignmentServiceTimeEntryId = null;
      }

    })
  }
}
